/** @jsx tsx */
import { tsx } from "@dojo/framework/core/vdom";
import { DNode } from "@dojo/framework/core/interfaces";
import * as _ from "lodash";
import { mapGroupedPointToShapeMatrix, manipulateDimensions } from "../../store/reducers/util";
import { AdjustHandlerPoint, IAdjustHandlerPointRenderProps } from "../components/AdjustHandlerPoint";
import { CroppingHandler, ICroppingHandlerRenderProps } from "../components/CroppingHandler";
import { ISelectionBoxRenderProps } from "../components/SelectionBox";
import { SelectionBoxPoint } from "../components/SelectionBoxPoint";
import { IGeoBoundaryMeta } from "../interfaces/model/dataStructures/GeoBoundaryMeta";
import { IPageObjectTypes, PageObjectScalePointType } from "../interfaces/model/enumerations/index";
import { IShapeGuide } from "../interfaces/model/geometry/ShapeGuide";
import { IXYAdjustHandle } from "../interfaces/model/geometry/XYAdjustHandle";
import { PageObjectType } from "../interfaces/model/Types";
import { IPosition } from "../meta/Drag/index";
import { isNear } from "../util";
import { IGroupShapeProperties } from "../interfaces/model/properties";

/**
 * Selection controls for adjust handlers
 * @param pageObject {PageObjectType} Selected page object
 * @param adjustHandlers Adjust handlers for selected page object (preset shape, to be specific)
 * @returns Array of adjust handler points
 */
export const adjust = (
	adjustHandlers: IXYAdjustHandle[],
	avList: IShapeGuide[],
	canvasContainerEl: HTMLElement,
	pageObject: PageObjectType,
	setShapeAdjustValues: IAdjustHandlerPointRenderProps["setShapeAdjustValues"],
	geoBoundaryMeta: IGeoBoundaryMeta): DNode[] =>
	_.map(adjustHandlers, (adjustHandler) => (
		<AdjustHandlerPoint
			adjustHandler={adjustHandler}
			avList={avList}
			canvasContainerEl={canvasContainerEl}
			geoBoundaryMeta={geoBoundaryMeta}
			key={pageObject.shapeId}
			pageObject={pageObject}
			setShapeAdjustValues={setShapeAdjustValues}
		/>
	));

/**
 * Selection controls for cropping
 * @param pageObject Selected page object
 * @param scaleObjects Callback to scale objects
 * @returns Array of cropping controls
 */
export const cropping = (
	pageObject: PageObjectType,
	scaleObjects: (...args: any[]) => void,
	setAllAspectRatios: ISelectionBoxRenderProps["setAllAspectRatios"]): DNode[] => {
	const { cx, cy } = pageObject.spPr.xfrm.ext;
	let key: number = 0;
	const croppingPoints: ICroppingHandlerRenderProps[] = [
		{
			coordinate: { x: 0, y: 0 }, key: key++, position: PageObjectScalePointType.CORNER_LEFT_TOP,
			scaleObjects, pageObject, setAllAspectRatios,
		},
		{
			coordinate: { x: cx / 2, y: 0 }, key: key++, position: PageObjectScalePointType.SIDE_TOP,
			scaleObjects, pageObject, setAllAspectRatios,
		},
		{
			coordinate: { x: cx, y: 0 }, key: key++, position: PageObjectScalePointType.CORNER_RIGHT_TOP,
			scaleObjects, pageObject, setAllAspectRatios,
		},
		{
			coordinate: { x: 0, y: cy / 2 }, key: key++, position: PageObjectScalePointType.SIDE_LEFT,
			scaleObjects, pageObject, setAllAspectRatios,
		},
		{
			coordinate: { x: cx, y: cy / 2 }, key: key++, position: PageObjectScalePointType.SIDE_RIGHT,
			scaleObjects, pageObject, setAllAspectRatios,
		},
		{
			coordinate: { x: 0, y: cy }, key: key++, position: PageObjectScalePointType.CORNER_LEFT_BOTTOM,
			scaleObjects, pageObject, setAllAspectRatios,
		},
		{
			coordinate: { x: cx / 2, y: cy }, key: key++, position: PageObjectScalePointType.SIDE_BOTTOM,
			scaleObjects, pageObject, setAllAspectRatios,

		},
		{
			coordinate: { x: cx, y: cy }, key: key++, position: PageObjectScalePointType.CORNER_RIGHT_BOTTOM,
			scaleObjects, pageObject, setAllAspectRatios,
		},
	];
	return croppingPoints.map((point) => <CroppingHandler {...point} />);
};

/**
 * Standard selection controls (for object scaling)
 * @param pageObject Selected page object
 * @param scaleObjects Callback method to scale objects
 * @returns Array of standard selection controls
 */
export const standard = (
	canvasContainerEl: HTMLElement,
	isPanning: boolean,
	setAutosize: (...args: any[]) => void,
	pageObject: PageObjectType,
	scaleObjects: (...args: any[]) => void,
	setAllAspectRatios: (...args: any[]) => void,
	isTempShape: boolean,
	groupObject?: PageObjectType,
	transformOrigin?: IPosition): DNode[] => {
	const HANDLER_DIMENSION = 6;
	const HANDLER_CENTER = 3;
	const TOLERANCE = 22.5;
	const spPr = pageObject.spPr;
	const { locked = false } = spPr;
	const strokeColor: string = locked ? "red" : "blue";
	const { ext } = spPr.xfrm;
	const { cx, cy } = ext;
	const rotation = (360 + spPr.xfrm.rot) % 360;
	let ltrbCornerCss = "c8_canvas_corner_nwse";
	let rtlbCornerCss = "c8_canvas_corner_nesw";
	let tbSideCss = "c8_canvas_v_resize";
	let lrSideCss = "c8_canvas_h_resize";

	if (spPr.xfrm.flipH) {
		ltrbCornerCss = "c8_canvas_corner_nesw";
		rtlbCornerCss = "c8_canvas_corner_nwse";
	}

	if (isNear(rotation, 45, TOLERANCE) || isNear(rotation, 225, TOLERANCE)) {
		if (spPr.xfrm.flipH) {
			ltrbCornerCss = "c8_canvas_h_resize";
			rtlbCornerCss = "c8_canvas_v_resize";
		} else {
			ltrbCornerCss = "c8_canvas_v_resize";
			rtlbCornerCss = "c8_canvas_h_resize";
		}

		tbSideCss = "c8_canvas_corner_nesw";
		lrSideCss = "c8_canvas_corner_nwse";
	} else if (isNear(rotation, 90, TOLERANCE) || isNear(rotation, 270, TOLERANCE)) {
		if (spPr.xfrm.flipH) {
			ltrbCornerCss = "c8_canvas_corner_nwse";
			rtlbCornerCss = "c8_canvas_corner_nesw";
		} else {
			ltrbCornerCss = "c8_canvas_corner_nesw";
			rtlbCornerCss = "c8_canvas_corner_nwse";
		}

		tbSideCss = "c8_canvas_h_resize";
		lrSideCss = "c8_canvas_v_resize";
	} else if (isNear(rotation, 135, TOLERANCE) || isNear(rotation, 315, TOLERANCE)) {
		if (spPr.xfrm.flipH) {
			ltrbCornerCss = "c8_canvas_v_resize";
			rtlbCornerCss = "c8_canvas_h_resize";
		} else {
			ltrbCornerCss = "c8_canvas_h_resize";
			rtlbCornerCss = "c8_canvas_v_resize";
		}

		tbSideCss = "c8_canvas_corner_nwse";
		lrSideCss = "c8_canvas_corner_nesw";
	}

	/** draw points for resizing */
	let resizeBoxPoints: any[] = [
		{ css: ltrbCornerCss, locked, scalePointType: PageObjectScalePointType.CORNER_LEFT_TOP, x: 0, y: 0 },
		{ css: rtlbCornerCss, locked, scalePointType: PageObjectScalePointType.CORNER_RIGHT_TOP, x: cx, y: 0 },
		{ css: ltrbCornerCss, locked, scalePointType: PageObjectScalePointType.CORNER_RIGHT_BOTTOM, x: cx, y: cy },
		{ css: rtlbCornerCss, locked, scalePointType: PageObjectScalePointType.CORNER_LEFT_BOTTOM, x: 0, y: cy },
	];
	// add side resizing points if object is not of type PICTURE
	if (pageObject.type === IPageObjectTypes.SHAPE || pageObject.type === IPageObjectTypes.CONNECTION_SHAPE) {
		resizeBoxPoints.push(
			{ css: tbSideCss, locked, scalePointType: PageObjectScalePointType.SIDE_TOP, x: cx / 2, y: 0 },
			{ css: lrSideCss, locked, scalePointType: PageObjectScalePointType.SIDE_RIGHT, x: cx, y: cy / 2 },
			{ css: tbSideCss, locked, scalePointType: PageObjectScalePointType.SIDE_BOTTOM, x: cx / 2, y: cy },
			{ css: lrSideCss, locked, scalePointType: PageObjectScalePointType.SIDE_LEFT, x: 0, y: cy / 2 },
		);
		// Only side-left and side-right selection points should be visible if table shape,
		// but position them vertically centered.
		if (pageObject.isTableShape) {
			resizeBoxPoints = [
				{ css: lrSideCss, locked, scalePointType: PageObjectScalePointType.SIDE_RIGHT, x: cx, y: cy / 2 },
				{ css: lrSideCss, locked, scalePointType: PageObjectScalePointType.SIDE_LEFT, x: 0, y: cy / 2 },
			];
		}
		// Only side-left and side-right selection points should be visible if line is horizontal
		// (height is zero).
		if (cy === 0 && pageObject.type === IPageObjectTypes.CONNECTION_SHAPE) {
			resizeBoxPoints = [
				{ css: lrSideCss, locked, scalePointType: PageObjectScalePointType.SIDE_LEFT, x: 0, y: 0 },
				{ css: lrSideCss, locked, scalePointType: PageObjectScalePointType.SIDE_RIGHT, x: cx, y: 0 },
			];
		}
		// Only side-top and side-bottom selection points should be visible if line is vertical
		// (width is zero).
		if (cx === 0 && pageObject.type === IPageObjectTypes.CONNECTION_SHAPE) {
			resizeBoxPoints = [
				{ css: tbSideCss, locked, scalePointType: PageObjectScalePointType.SIDE_TOP, x: 0, y: 0 },
				{ css: tbSideCss, locked, scalePointType: PageObjectScalePointType.SIDE_BOTTOM, x: 0, y: cy },
			];
		}
	}

	return resizeBoxPoints.map((point, index) => {
		// Get point with respect to center, map point, add to center.
		const shapeCenter = transformOrigin ? transformOrigin : { x: cx / 2, y: cy / 2 };
		const position = { x: point.x - shapeCenter.x, y: point.y - shapeCenter.y };
		const mappedPosition = mapGroupedPointToShapeMatrix(groupObject, pageObject, position, false);

		let offX = spPr.xfrm.off.x;
		let offY = spPr.xfrm.off.y;

		if (groupObject !== undefined) {
			const subObj = manipulateDimensions.calculateSubgroupAbsoluteOffsets (spPr, groupObject.spPr as any as IGroupShapeProperties);

			offX = subObj.offX;
			offY = subObj.offY;
		}

		const positionInCanvas = {
			x: (shapeCenter.x + mappedPosition.x + offX),
			y: (shapeCenter.y + mappedPosition.y + offY),
		};

		return (
			<SelectionBoxPoint
				canvasContainerEl={canvasContainerEl}
				css={`${point.css}`}
				groupObject={groupObject}
				height={HANDLER_DIMENSION}
				isPanning={isPanning}
				isTempShape={isTempShape}
				key={index}
				locked={locked}
				pageObject={pageObject}
				positionInCanvas={positionInCanvas}
				scaleObjects={scaleObjects}
				scalePointType={point.scalePointType}
				setAllAspectRatios={setAllAspectRatios}
				setShapeAutosizeValues={setAutosize}
				stroke={`${strokeColor}`}
				width={HANDLER_DIMENSION}
				x={point.x - HANDLER_CENTER}
				y={point.y - HANDLER_CENTER}
			/>
		);
	});
};
